import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import {
  SEGURADORAS,
  normalizarFiltroSeguradora,
  type FiltroSeguradora as Filtro,
  type Seguradora,
} from "./seguradoras";
import { listarSeguradorasAtivas } from "./useTabelaSeguros";
import { LogoSeguradora } from "./LogoSeguradora";

/**
 * Filtro de seguradora das telas de Seguros, guardado em `?seguradora=`.
 *
 * Fica na URL de propósito: quem abre "Cobrança" já filtrado em MAG e clica
 * numa apólice volta com o mesmo recorte, e o link é compartilhável. Sem o
 * parâmetro, o recorte é "Todas".
 */
export const useSeguradoraFiltro = () => {
  const [params, setParams] = useSearchParams();
  const seguradora = normalizarFiltroSeguradora(params.get("seguradora"));

  const definir = useCallback(
    (nova: Filtro) => {
      setParams(
        (atual) => {
          const proximo = new URLSearchParams(atual);
          if (nova === "todas") proximo.delete("seguradora");
          else proximo.set("seguradora", nova);
          return proximo;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  return { seguradora, definir };
};

/**
 * Lista de seguradoras para os chips. Tenta `seguros_seguradoras` (ativas);
 * se a tabela não existir ainda ou vier vazia, usa a lista estática. Nunca
 * deixa a tela sem filtro.
 */
const useSeguradorasAtivas = () => {
  const [lista, setLista] = useState<Seguradora[]>(SEGURADORAS);
  useEffect(() => {
    let ativo = true;
    listarSeguradorasAtivas()
      .then((rows) => {
        if (ativo && rows.length > 0) setLista(rows);
      })
      .catch(() => {});
    return () => {
      ativo = false;
    };
  }, []);
  return lista;
};

// Chips Premium North. O ativo fica claro com borda marinho (e não cheio): a
// arte das seguradoras é escura e sumiria sobre o marinho.
const CHIP_BASE =
  "inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors";
const CHIP_ATIVO = "border-marinho bg-[#EEF1F6] text-marinho font-semibold ring-1 ring-marinho";
const CHIP_INATIVO = "border-border bg-card text-foreground hover:bg-muted";

export const FiltroSeguradora = ({
  valor,
  aoMudar,
}: {
  valor: Filtro;
  aoMudar: (v: Filtro) => void;
}) => {
  const lista = useSeguradorasAtivas();
  // "Todas" não tem logo — é o recorte, não uma marca.
  const opcoes: { valor: Filtro; rotulo: string; codigo?: string }[] = [
    { valor: "todas" as Filtro, rotulo: "Todas" },
    ...lista.map((s): { valor: Filtro; rotulo: string; codigo: string } => ({
      valor: normalizarFiltroSeguradora(s.codigo),
      rotulo: s.nome,
      codigo: s.codigo,
    })),
  ].filter((o, i, arr) => arr.findIndex((x) => x.valor === o.valor) === i);

  return (
    <div className="flex items-center gap-1" role="group" aria-label="Seguradora">
      {opcoes.map((o) => (
        <button
          key={o.valor}
          type="button"
          data-slot="chip"
          onClick={() => aoMudar(o.valor)}
          aria-pressed={valor === o.valor}
          aria-label={o.rotulo}
          className={`${CHIP_BASE} ${valor === o.valor ? CHIP_ATIVO : CHIP_INATIVO}`}
        >
          {/* Com logo, o nome não se repete ao lado: a arte das duas seguradoras
              é a própria palavra da marca. O rótulo vai no aria-label. */}
          {o.codigo
            ? <LogoSeguradora codigo={o.codigo} nome={o.rotulo} tamanho="xs" sozinha />
            : o.rotulo}
        </button>
      ))}
    </div>
  );
};
